/** Public support line when the API does not expose a dedicated delivery manager number. */
export const FALLBACK_DELIVERY_MANAGER_PHONE = "+919023106911";

function digitsOnly(s: string): string {
  return s.replace(/\D/g, "");
}

/**
 * Phone for "Call Delivery Manager" — never the partner / user login number.
 * Uses explicit API fields (or store line), otherwise {@link FALLBACK_DELIVERY_MANAGER_PHONE}.
 */
export function getDeliveryManagerPhone(raw: Record<string, unknown> | null | undefined): string {
  if (!raw || typeof raw !== "object") return FALLBACK_DELIVERY_MANAGER_PHONE;

  const partner = raw.partner as Record<string, unknown> | undefined;

  const candidates: unknown[] = [
    raw.delivery_manager_phone,
    raw.delivery_manager_contact,
    raw.manager_phone,
    raw.support_phone,
    raw.emergency_contact,
    raw.emergencyContact,
    partner?.store_phone,
    partner?.manager_phone,
    partner?.delivery_manager_phone,
    partner?.support_phone,
  ];

  for (const c of candidates) {
    if (typeof c !== "string") continue;
    const t = c.trim();
    if (digitsOnly(t).length >= 10) return t;
  }

  return FALLBACK_DELIVERY_MANAGER_PHONE;
}

function readStoredProfile(): Record<string, unknown> | undefined {
  try {
    const raw = localStorage.getItem("profile");
    if (!raw) return undefined;
    return JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return undefined;
  }
}

/** Open tel: link for the delivery manager (profile API or fallback). */
export function callDeliveryManagerPhone(
  onError?: (message: string) => void
): void {
  const phone = getDeliveryManagerPhone(readStoredProfile());
  const clean = phone.replace(/[^\d+]/g, "");
  if (clean.length >= 10) {
    window.location.href = `tel:${clean}`;
  } else {
    onError?.("Manager phone number not available");
  }
}

/**
 * Emergency row in the drawer — only real emergency fields, not login / user phone.
 * If API echoes the same number as the account phone, show an em dash instead.
 */
export function getEmergencyContactDisplay(raw: Record<string, unknown> | null | undefined): string {
  if (!raw || typeof raw !== "object") return "—";

  const ecRaw = raw.emergency_contact ?? raw.emergencyContact;
  if (typeof ecRaw !== "string" || !ecRaw.trim()) return "—";

  const ecDigits = digitsOnly(ecRaw);
  if (ecDigits.length < 10) return "—";

  const user = raw.user as Record<string, unknown> | undefined;
  const userPhone = typeof user?.phone === "string" ? digitsOnly(user.phone) : "";
  const flatPhone = typeof raw.phone === "string" ? digitsOnly(raw.phone) : "";

  if (userPhone && ecDigits === userPhone) return "—";
  if (flatPhone && ecDigits === flatPhone) return "—";

  return ecRaw.trim();
}
